import { motion } from 'framer-motion';
import { Download } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import DocumintLogoSVG from '../components/DocumintLogoSVG';
import SEOHead from '../components/SEOHead';

const PRESETS = {
    current: { d1: '#0077CC', d2: '#00B8E8', d3: '#00D9F5', m1: '#22BB74', m2: '#47D764', m3: '#A4F06B', t1: '#7C6CF7', t2: '#F07AC2', u1: '#11B4BE', u2: '#0B8A80', bg: '#14305A', transparent: true },
    dark: { d1: '#0077CC', d2: '#00B8E8', d3: '#00D9F5', m1: '#22BB74', m2: '#47D764', m3: '#A4F06B', t1: '#7C6CF7', t2: '#F07AC2', u1: '#11B4BE', u2: '#0B8A80', bg: '#14305A', transparent: false },
    light: { d1: '#0077CC', d2: '#00B8E8', d3: '#00D9F5', m1: '#22BB74', m2: '#47D764', m3: '#A4F06B', t1: '#7C6CF7', t2: '#F07AC2', u1: '#11B4BE', u2: '#0B8A80', bg: '#FFFFFF', transparent: true },
    navy: { d1: '#0D1E3A', d2: '#14305A', d3: '#1E4A80', m1: '#0D1E3A', m2: '#14305A', m3: '#1E4A80', t1: '#7C6CF7', t2: '#F07AC2', u1: '#0D9AA3', u2: '#0B6A72', bg: '#FFFFFF', transparent: true },
    purple: { d1: '#6244E8', d2: '#3E90E8', d3: '#00D9F5', m1: '#22BB74', m2: '#47D764', m3: '#A4F06B', t1: '#9B6CF7', t2: '#F07AC2', u1: '#9B6CF7', u2: '#6244E8', bg: '#0E0A20', transparent: false },
    mono: { d1: '#1A1A2E', d2: '#2E2E50', d3: '#44447A', m1: '#1A1A2E', m2: '#2E2E50', m3: '#44447A', t1: '#5A5A8A', t2: '#8A8AB8', u1: '#3A3A6A', u2: '#1A1A2E', bg: '#FFFFFF', transparent: true },
};

const PRESET_LABELS = {
    current: 'Reset',
    dark: 'Dark indigo',
    light: 'Light teal',
    navy: 'Navy mono',
    purple: 'Purple glow',
    mono: 'Charcoal',
};

const WORDMARK_FIELDS = [
    { id: 'd1', label: '"docu" start' },
    { id: 'd2', label: '"docu" mid' },
    { id: 'd3', label: '"docu" end' },
    { id: 'm1', label: '"mint" start' },
    { id: 'm2', label: '"mint" mid' },
    { id: 'm3', label: '"mint" end' },
    { id: 't1', label: 'Tagline start' },
    { id: 't2', label: 'Tagline end' },
];

const ICON_FIELDS = [
    { id: 'u1', label: 'D upper/stem' },
    { id: 'u2', label: 'D upper/stem end' },
    { id: 'bg', label: 'Background' },
];

// Base logo dimensions (viewBox units == px at 1x)
const BASE_WIDTH = 520;
const BASE_HEIGHT = 160;

const PNG_EXPORTS = [
    { id: 'web', label: `Web (3×) — ${BASE_WIDTH * 3}×${BASE_HEIGHT * 3}`, scale: 3 },
    { id: 'creative', label: `Creative / Social (5×) — ${BASE_WIDTH * 5}×${BASE_HEIGHT * 5}`, scale: 5 },
    { id: 'banner', label: `Flex Banner (15×) — ${BASE_WIDTH * 15}×${BASE_HEIGHT * 15}`, scale: 15 },
];

const isValidHex = (value) => /^#[0-9a-fA-F]{6}$/.test(value);

const ColorField = ({ id, label, value, onChange }) => {
    const [draft, setDraft] = useState(value);

    useEffect(() => {
        setDraft(value);
    }, [value]);

    return (
        <div className="flex items-center gap-2 mb-2.5">
            <label htmlFor={`c-${id}`} className="flex-1 text-sm text-gray-400 whitespace-nowrap">
                {label}
            </label>
            <div
                className="relative w-7 h-7 rounded-md border border-white/10 overflow-hidden flex-shrink-0 cursor-pointer"
                style={{ background: value }}
            >
                <input
                    id={`c-${id}`}
                    type="color"
                    value={value}
                    onChange={(e) => {
                        onChange(e.target.value);
                        setDraft(e.target.value);
                    }}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                />
            </div>
            <input
                className="w-20 text-xs px-1.5 py-1 rounded-md border border-white/10 bg-surface-300 text-gray-200 font-mono focus:outline-none focus:ring-1 focus:ring-neon-400"
                value={draft}
                onChange={(e) => {
                    const v = e.target.value;
                    setDraft(v);
                    if (isValidHex(v)) onChange(v);
                }}
                onBlur={() => setDraft(value)}
            />
        </div>
    );
};

const LogoEditor = () => {
    const [colors, setColors] = useState(PRESETS.current);
    const [pngExportId, setPngExportId] = useState('web');
    const svgRef = useRef(null);

    const updateColor = (id, value) => {
        setColors((prev) => ({ ...prev, [id]: value }));
    };

    const applyPreset = (name) => {
        setColors(PRESETS[name]);
    };

    const serializeSVG = ({ width, height } = {}) => {
        const svgEl = svgRef.current;
        if (!svgEl) return '';
        const clone = svgEl.cloneNode(true);
        clone.setAttribute('width', width ?? BASE_WIDTH);
        clone.setAttribute('height', height ?? BASE_HEIGHT);
        return new XMLSerializer().serializeToString(clone);
    };

    const downloadSVG = () => {
        const svgString = serializeSVG();
        const blob = new Blob([svgString], { type: 'image/svg+xml' });
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = 'documint-logo.svg';
        a.click();
        URL.revokeObjectURL(a.href);
    };

    const downloadPNG = () => {
        const preset = PNG_EXPORTS.find((p) => p.id === pngExportId) || PNG_EXPORTS[0];
        const width = BASE_WIDTH * preset.scale;
        const height = BASE_HEIGHT * preset.scale;
        const svgString = serializeSVG({ width, height });
        const img = new Image();
        const blob = new Blob([svgString], { type: 'image/svg+xml' });
        const url = URL.createObjectURL(blob);
        img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0, width, height);
            canvas.toBlob((pngBlob) => {
                const a = document.createElement('a');
                a.href = URL.createObjectURL(pngBlob);
                a.download = `documint-logo-${preset.id}-${width}x${height}.png`;
                a.click();
                URL.revokeObjectURL(a.href);
                URL.revokeObjectURL(url);
            }, 'image/png');
        };
        img.src = url;
    };

    return (
        <>
            <SEOHead
                title="Logo Color Editor"
                description="Customize the Documint logo's colors and download it as SVG or high-resolution PNG for web, creatives, or flex banners."
            />
            <section className="section-padding hero-grid min-h-[calc(100vh-4rem)]">
                <div className="container-custom max-w-5xl">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        className="mb-10 text-center"
                    >
                        <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">
                            Logo Color Editor
                        </h1>
                        <p className="text-gray-400 max-w-2xl mx-auto">
                            Fine-tune the Documint wordmark and icon colors, apply a preset, and export as SVG or high-resolution PNG.
                        </p>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.1 }}
                        className={`rounded-2xl p-6 sm:p-8 mb-6 flex items-center justify-center min-h-[200px] ${
                            colors.transparent
                                ? 'bg-[repeating-conic-gradient(#1f2937_0%_25%,#374151_0%_50%)] bg-[length:16px_16px]'
                                : ''
                        }`}
                    >
                        <DocumintLogoSVG
                            ref={svgRef}
                            colors={colors}
                            uid="editor"
                            style={{ maxWidth: 560 }}
                        />
                    </motion.div>

                    <div className="grid md:grid-cols-2 gap-4 mb-6">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.15 }}
                            className="glass p-5"
                        >
                            <h3 className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-3">
                                Wordmark
                            </h3>
                            {WORDMARK_FIELDS.map(({ id, label }) => (
                                <ColorField
                                    key={id}
                                    id={id}
                                    label={label}
                                    value={colors[id]}
                                    onChange={(v) => updateColor(id, v)}
                                />
                            ))}
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.2 }}
                            className="glass p-5"
                        >
                            <h3 className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-3">
                                D icon &amp; background
                            </h3>
                            {ICON_FIELDS.map(({ id, label }) => (
                                <ColorField
                                    key={id}
                                    id={id}
                                    label={label}
                                    value={colors[id]}
                                    onChange={(v) => updateColor(id, v)}
                                />
                            ))}
                            <div className="flex items-center gap-2 mb-2.5">
                                <label htmlFor="chk-trans" className="flex-1 text-sm text-gray-400">
                                    Transparent bg
                                </label>
                                <input
                                    id="chk-trans"
                                    type="checkbox"
                                    checked={colors.transparent}
                                    onChange={(e) => updateColor('transparent', e.target.checked)}
                                    className="w-[18px] h-[18px] cursor-pointer accent-neon-400"
                                />
                            </div>

                            <h3 className="text-xs font-medium text-gray-400 uppercase tracking-wider mt-4 mb-3">
                                Presets
                            </h3>
                            <div className="flex gap-2 flex-wrap">
                                {Object.keys(PRESETS).map((name) => (
                                    <button
                                        key={name}
                                        type="button"
                                        onClick={() => applyPreset(name)}
                                        className="text-xs px-2.5 py-1.5 rounded-md border border-white/10 bg-surface-300 text-gray-300 hover:bg-surface-200 transition-colors"
                                    >
                                        {PRESET_LABELS[name]}
                                    </button>
                                ))}
                            </div>
                        </motion.div>
                    </div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.25 }}
                        className="flex gap-3 flex-wrap items-center"
                    >
                        <button type="button" onClick={downloadSVG} className="btn-secondary inline-flex items-center gap-2 !py-2.5 !px-4">
                            <Download size={16} /> Download SVG
                        </button>

                        <div className="flex items-center gap-2">
                            <select
                                value={pngExportId}
                                onChange={(e) => setPngExportId(e.target.value)}
                                className="text-xs px-3 py-2.5 rounded-lg border border-white/10 bg-surface-300 text-gray-200 focus:outline-none focus:ring-1 focus:ring-neon-400"
                            >
                                {PNG_EXPORTS.map((p) => (
                                    <option key={p.id} value={p.id}>{p.label}</option>
                                ))}
                            </select>
                            <button type="button" onClick={downloadPNG} className="btn-secondary inline-flex items-center gap-2 !py-2.5 !px-4">
                                <Download size={16} /> Download PNG
                            </button>
                        </div>
                    </motion.div>
                </div>
            </section>
        </>
    );
};

export default LogoEditor;
